// src/pages/NewCampaign.jsx
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import ErrorToast from '../components/ErrorToast';
import Toast from '../components/Toast';
import FiltroImoveis from '../components/FiltroImoveis';
import ConfirmDialog from '../components/ConfirmDialog';
import './NewCampaign.css';
import { useNavigate } from 'react-router-dom';

const NewCampaign = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [toastMsg, setToastMsg] = useState('');
  const [error, setError] = useState('');

  const [tipos, setTipos] = useState([]);
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    data_inicio: '',
    tipo_campanha_ids: [],
    clientes: [],
  });

  // STEP 2 states
  const [imoveisFilters, setImoveisFilters] = useState({});
  const [imoveisPreview, setImoveisPreview] = useState([]);
  const [totalImoveis, setTotalImoveis] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(0);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [loadingClientes, setLoadingClientes] = useState(false);

  // Clientes preview + search + pending removal
  const [clientesData, setClientesData] = useState([]);
  const [searchCliente, setSearchCliente] = useState('');
  const [toRemove, setToRemove] = useState(null);

  // 1) Load tipos de campanha
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/tipos_campanha`,
        );
        setTipos(data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const toggleTipo = id => {
    setForm(f => {
      const s = new Set(f.tipo_campanha_ids);
      s.has(id) ? s.delete(id) : s.add(id);
      return { ...f, tipo_campanha_ids: Array.from(s) };
    });
  };

  const next = () => {
    if (step === 1 && (!form.nome.trim() || form.tipo_campanha_ids.length === 0)) {
      setError('Nome e ao menos um tipo são obrigatórios.');
      return;
    }
    setError('');
    setStep(s => s + 1);
  };
  const prev = () => {
    setError('');
    setStep(s => s - 1);
  };

  // STEP 2: fetch imóveis
  const buscarImoveis = async filters => {
    setLoadingImoveis(true);
    setError('');
    try {
      const { data } = await api.get(
        `/imoveis`,
        {
          params: { ...filters, page, pageSize },
        }
      );
      setImoveisPreview(data.data || []);
      setTotalPages(data.totalPages);
      setTotalImoveis(data.total);
    } catch {
      setError('Erro ao buscar imóveis.');
    } finally {
      setLoadingImoveis(false);
    }
  };

  const onFiltrosSubmit = filters => {
    setImoveisFilters(filters);
    setPage(1);
    buscarImoveis(filters);
  };

  // Confirm selection: fetch clientes
  const confirmClientes = async () => {
    setLoadingClientes(true);
    setError('');
    try {
      const res = await api.get(
        `/imoveis/clientes`,
        {
          params: imoveisFilters,
        }
      );
      const clientes = res.data || [];
      setForm(f => ({
        ...f,
        clientes: clientes.map(p => p.id),
      }));
      setClientesData(clientes);
      if (clientes.length === 0) {
        setError('Nenhum cliente encontrado para esses filtros.');
      }
    } catch {
      setError('Erro ao buscar clientes.');
    } finally {
      setLoadingClientes(false);
    }
  };

  // STEP 3: submit
  const handleSubmit = async () => {
    if (form.clientes.length === 0) {
      setError('Selecione ao menos um cliente.');
      return;
    }
    try {
      await api.post(
        `/campanhas`,
        {
          campanha: {
            nome: form.nome,
            descricao: form.descricao,
            data_inicio: form.data_inicio || null,
            tipo_campanha_ids: form.tipo_campanha_ids,
          },
          ids_clientes: clientesData.map(p => p.id), // usa o array exibido
        },
      );
      setToastMsg('Campanha criada com sucesso!');
            navigate('/campanhas');

    } catch {
      setError('Erro ao criar campanha.');
    }
  };

  return (
    <div className="new-campaign-container">
      <h1>Nova Campanha</h1>
      {error && <ErrorToast message={error} onClose={() => setError('')} />}
      {toastMsg && <Toast message={toastMsg} onClose={() => setToastMsg('')} />}

      <div className="steps-indicator">
        {['Detalhes', 'Clientes', 'Revisão'].map((label, i) => (
          <div key={i} className={`step ${step === i + 1 ? 'active' : ''}`}>
            {i + 1}. {label}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div className="step-content">
          <label>Nome*</label>
          <input
            name="nome"
            value={form.nome}
            onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
          />

          <label>Descrição</label>
          <textarea
            name="descricao"
            value={form.descricao}
            onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
          />

          <label>Data Início</label>
          <input
            type="datetime-local"
            name="data_inicio"
            value={form.data_inicio}
            onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))}
          />

          <div className="checkbox-group">
            <label className="group-label">Tipos de Campanha*</label>
            <div className="checkbox-list">
              {tipos.map(t => (
                <label key={t.id} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={form.tipo_campanha_ids.includes(t.id)}
                    onChange={() => toggleTipo(t.id)}
                  />
                  <span>{t.nome}</span>
                </label>
              ))}
            </div>
            {form.tipo_campanha_ids.length === 0 && (
              <p className="field-error">Selecione ao menos um tipo.</p>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="step-content">
          <h2>Selecione Clientes via filtros de imóveis</h2>
          <FiltroImoveis
            onSearch={onFiltrosSubmit}
            page={page}
            setPage={setPage}
            data={imoveisPreview}
            totalPages={totalPages}
            loading={loadingImoveis}
          />
          <button
            className="button importar-btn"
            onClick={confirmClientes}
            disabled={loadingClientes || totalImoveis === 0}
          >
            {loadingClientes
              ? 'Buscando...'
              : `Confirmar seleção (${totalImoveis} imóveis)`}
          </button>

          {clientesData.length > 0 && (
            <>
              <label style={{ marginTop: '1rem' }}>Buscar Clientes:</label>
              <input
                type="text"
                placeholder="Filtrar..."
                value={searchCliente}
                onChange={e => setSearchCliente(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', margin: '0.5rem 0' }}
              />
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nome</th>
                      <th>Documento</th>
                      <th>Tipo</th>
                      <th>Origem</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesData
                      .filter(p =>
                        Object.values(p)
                          .some(val =>
                            String(val)
                              .toLowerCase()
                              .includes(searchCliente.toLowerCase())
                          )
                      )
                      .slice(0, 10)
                      .map(p => (
                        <tr key={p.id}>
                          <td>{p.id}</td>
                          <td>{p.nome}</td>
                          <td>{p.documento}</td>
                          <td>{p.tipo}</td>
                          <td>{p.contribuinte_origem || '-'}</td>
                          <td>
                            <button
                              className="button remove-btn"
                              onClick={() => setToRemove(p)}
                            >
                              Remover
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {step === 3 && (
        <div className="step-content">
          <h2>Revisão</h2>
          <div className="review-item">
            <strong>Nome:</strong> {form.nome}
          </div>
          <div className="review-item">
            <strong>Descrição:</strong> {form.descricao || '-'}
          </div>
          <div className="review-item">
            <strong>Data Início:</strong> {form.data_inicio || '-'}
          </div>
          <div className="review-item">
            <strong>Tipos de Campanha:</strong>{' '}
            {tipos
              .filter(t => form.tipo_campanha_ids.includes(t.id))
              .map(t => t.nome)
              .join(', ')}
          </div>
          <div className="review-item">
            <strong>Clientes selecionados:</strong> {form.clientes.length}
          </div>
        </div>
      )}

      <div className="buttons">
        {step > 1 && <button onClick={prev}>Voltar</button>}
        {step < 3 ? (
          <button onClick={next}>Próximo</button>
        ) : (
          <button onClick={handleSubmit}>Criar Campanha</button>
        )}
      </div>

      {toRemove && (
        <ConfirmDialog
          message={`Deseja remover o cliente "${toRemove.nome}"?`}
          onConfirm={() => {
            setForm(f => ({
              ...f,
              clientes: f.clientes.filter(id => id !== toRemove.id),
            }));
            setClientesData(prev =>
              prev.filter(item => item.id !== toRemove.id)
            );
            setToRemove(null);
          }}
          onCancel={() => setToRemove(null)}
        />
      )}
    </div>
  );
};

export default NewCampaign;